<template>
  <div class="perfil-page">
    <section class="profile-card">
      <div class="card-head">
        <h2>Perfil</h2>
        <p>Atualize foto e informações gerais da sua conta.</p>
      </div>

      <form class="profile-form" @submit.prevent="saveProfile">
        <p v-if="loadError" class="inline-error profile-banner">{{ loadError }}</p>
        <p v-if="loadingProfile" class="profile-muted">Carregando dados do perfil…</p>
        <p v-if="profileSaveError" class="inline-error profile-banner">{{ profileSaveError }}</p>

        <div class="photo-row">
          <div v-if="displayPhotoSrc" class="photo-avatar photo-avatar-image">
            <img :src="displayPhotoSrc" alt="Avatar do perfil" />
          </div>
          <div v-else class="photo-avatar">
            {{ initials }}
          </div>

          <div class="photo-meta">
            <div class="photo-actions">
              <button
                type="button"
                class="photo-btn"
                :disabled="loadingProfile || uploadingAvatar"
                @click="triggerPhotoUpload"
              >
                {{ uploadingAvatar ? 'Enviando…' : 'Trocar foto' }}
              </button>
              <button
                v-if="displayPhotoSrc"
                type="button"
                class="photo-btn photo-btn-danger"
                :disabled="loadingProfile || uploadingAvatar || removingAvatar"
                @click="removePhoto"
              >
                {{ removingAvatar ? 'Removendo…' : 'Remover foto' }}
              </button>
            </div>
            <p>JPEG, PNG ou WebP até 5MB</p>
            <input
              ref="photoInput"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              class="hidden-input"
              @change="handlePhotoChange"
            />
          </div>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label for="displayName">Nome</label>
            <input id="displayName" v-model="form.displayName" type="text" placeholder="Seu nome completo" />
          </div>

          <div class="form-group">
            <label for="email">Email</label>
            <input id="email" v-model="form.email" type="email" placeholder="joao@example.com" required />
          </div>

          <div class="form-group">
            <label for="companyName">Nome da empresa</label>
            <input id="companyName" v-model="form.companyName" type="text" placeholder="Nome da empresa" />
          </div>

          <div class="form-group">
            <label for="accountId">ID da conta</label>
            <input id="accountId" :value="currentUser?.userId || ''" type="text" disabled />
          </div>

          <div class="form-group">
            <label for="username">Nome de usuário</label>
            <input id="username" v-model="form.username" type="text" placeholder="usuario.login" />
          </div>

          <div class="form-group">
            <label for="phone">Telefone</label>
            <input
              id="phone"
              v-model="form.phone"
              type="text"
              placeholder="(00) 00000-0000"
              maxlength="15"
              @input="formatPhoneInput"
            />
          </div>

          <div class="form-group">
            <label for="role">Cargo</label>
            <input id="role" v-model="form.role" type="text" placeholder="Ex: Advogado(a)" />
          </div>

          <div class="form-group">
            <label for="cpf">CPF</label>
            <input
              id="cpf"
              v-model="form.cpf"
              type="text"
              maxlength="14"
              placeholder="000.000.000-00"
              inputmode="numeric"
              autocomplete="off"
              @input="formatCpfInput"
            />
          </div>

          <div class="form-group">
            <label for="bornDate">Data de nascimento</label>
            <input id="bornDate" v-model="form.bornDate" type="date" />
          </div>

          <div class="form-group">
            <label for="street">Rua / endereço</label>
            <input id="street" v-model="form.street" type="text" placeholder="Rua, número" />
          </div>

          <div class="form-group">
            <label for="neighborhood">Bairro</label>
            <input id="neighborhood" v-model="form.neighborhood" type="text" />
          </div>

          <div class="form-group">
            <label for="state">Estado</label>
            <input id="state" v-model="form.state" type="text" placeholder="UF" maxlength="2" />
          </div>

          <div class="form-group">
            <label for="country">País</label>
            <input id="country" v-model="form.country" type="text" />
          </div>

          <div class="form-group full-width">
            <label for="notes">Informações gerais</label>
            <textarea
              id="notes"
              v-model="form.notes"
              rows="4"
              placeholder="Adicione observações sobre o perfil"
            />
          </div>
        </div>

        <div class="actions">
          <button type="submit" class="primary-action" :disabled="loadingProfile || savingProfile">
            {{ savingProfile ? 'Salvando…' : 'Salvar informações' }}
          </button>
        </div>
      </form>
    </section>

    <section class="profile-card password-card">
      <div class="card-head">
        <h2>Alterar Senha</h2>
        <p>Defina uma nova senha de acesso.</p>
      </div>

      <form class="password-form" @submit.prevent="savePassword">
        <div class="password-grid">
          <div class="form-group full-width">
            <label for="currentPassword">Senha atual</label>
            <input id="currentPassword" v-model="passwordForm.currentPassword" type="password" placeholder="Digite sua senha atual" />
          </div>

          <div class="form-group">
            <label for="newPassword">Nova senha</label>
            <input id="newPassword" v-model="passwordForm.newPassword" type="password" placeholder="Digite a nova senha" />
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirmar nova senha</label>
            <input id="confirmPassword" v-model="passwordForm.confirmPassword" type="password" placeholder="Repita a nova senha" />
          </div>
        </div>

        <p v-if="passwordError" class="inline-error">{{ passwordError }}</p>

        <div class="actions password-actions">
          <button type="submit" class="primary-action" :disabled="loadingProfile || savingPassword">
            {{ savingPassword ? 'Atualizando…' : 'Atualizar senha' }}
          </button>
        </div>
      </form>
    </section>

    <div v-if="showSuccessModal" class="modal" @click.self="showSuccessModal = false">
      <div
        class="modal-content"
        :class="{ 'modal-content--avatar-upload-ok': modalVariant === 'avatarSuccess' }"
      >
        <h2>{{ modalTitle }}</h2>
        <p class="modal-message" :class="modalVariant">{{ modalMessage }}</p>
        <div class="modal-actions">
          <button type="button" class="btn btn-primary" @click="showSuccessModal = false">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import type { AuthUser, BackendUser, UpdateUserDto } from '@/types'
import { ApiException } from '@/services/api'
import { authService } from '@/services/auth.service'
import { userService } from '@/services/user.service'

interface ProfileForm {
  email: string
  username: string
  displayName: string
  phone: string
  companyName: string
  role: string
  notes: string
  /** URL pública R2 vinda do payload do usuário (`photoUrl`); usar em `<img :src>`. */
  photoUrl: string
  /** Legado / data URL local; omitir quando `photoUrl` existir. */
  avatar: string
  cpf: string
  bornDate: string
  street: string
  state: string
  country: string
  neighborhood: string
}

function splitDisplayName(displayName: string): { firstName: string; lastName: string } {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) {
    return { firstName: '', lastName: '' }
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' }
  }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

function bornDateToInput(isoOrDate: string): string {
  if (!isoOrDate) return ''
  return isoOrDate.includes('T') ? isoOrDate.slice(0, 10) : isoOrDate.slice(0, 10)
}

function mergeAuthUserFromBackend(
  backend: BackendUser,
  local: AuthUser | null,
  extras: ProfileForm
): AuthUser {
  return {
    userId: local?.userId,
    email: backend.email,
    username: backend.username,
    displayName: `${backend.firstName} ${backend.lastName}`.trim(),
    cpf: backend.cpf,
    bornDate: backend.bornDate,
    street: backend.street ?? '',
    state: backend.state ?? '',
    country: backend.country ?? '',
    neighborhood: backend.neighborhood ?? '',
    phone: extras.phone,
    companyName: extras.companyName,
    role: extras.role,
    notes: extras.notes,
    photoUrl: extras.photoUrl?.trim() ? extras.photoUrl.trim() : (backend.photoUrl ?? null),
    avatar: extras.avatar
  }
}

export default {
  name: 'Perfil',
  setup() {
    const store = useStore()
    const currentUser = computed<AuthUser | null>(() => store.state.currentUser)
    const showSuccessModal = ref(false)
    const modalTitle = ref('')
    const modalMessage = ref('')
    const modalVariant = ref<'success' | 'avatarSuccess' | 'error' | 'info'>('info')
    const passwordError = ref('')
    const photoInput = ref<HTMLInputElement | null>(null)
    const loadingProfile = ref(false)
    const savingProfile = ref(false)
    const savingPassword = ref(false)
    const loadError = ref('')
    const profileSaveError = ref('')
    const uploadingAvatar = ref(false)
    const removingAvatar = ref(false)

    const form = ref<ProfileForm>({
      email: currentUser.value?.email || '',
      username: currentUser.value?.username || '',
      displayName: currentUser.value?.displayName || '',
      phone: '',
      companyName: currentUser.value?.companyName || '',
      role: currentUser.value?.role || '',
      notes: currentUser.value?.notes || '',
      photoUrl: currentUser.value?.photoUrl || '',
      avatar: currentUser.value?.avatar || '',
      cpf: '',
      bornDate: '',
      street: currentUser.value?.street || '',
      state: currentUser.value?.state || '',
      country: currentUser.value?.country || '',
      neighborhood: currentUser.value?.neighborhood || ''
    })

    const passwordForm = ref({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })

    const initials = computed(() => {
      const source = form.value.displayName || form.value.email || 'U'
      return source
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || '')
        .join('')
    })

    /** URL da API ou pré-visualização local (data URL). */
    const displayPhotoSrc = computed(() => {
      const url = form.value.photoUrl?.trim()
      if (url) return url
      return form.value.avatar?.trim() || ''
    })

    const openModal = (
      title: string,
      message: string,
      variant: 'success' | 'avatarSuccess' | 'error' | 'info' = 'info'
    ) => {
      modalTitle.value = title
      modalMessage.value = message
      modalVariant.value = variant
      showSuccessModal.value = true
    }

    const bustPhotoUrl = (url: string): string => {
      const trimmed = url.trim()
      if (!trimmed) return ''
      try {
        const parsed = new URL(trimmed, window.location.origin)
        parsed.searchParams.set('v', String(Date.now()))
        return parsed.toString()
      } catch {
        const sep = trimmed.includes('?') ? '&' : '?'
        return `${trimmed}${sep}v=${Date.now()}`
      }
    }

    const formatPhone = (phone: string) => {
      if (!phone) return ''

      const numbers = phone.replace(/\D/g, '')

      if (numbers.length === 11) {
        return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
      }

      if (numbers.length === 10) {
        return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
      }

      return phone
    }

    const formatPhoneInput = (event: Event) => {
      const target = event.target as HTMLInputElement
      let value = target.value.replace(/\D/g, '')

      if (value.length <= 11) {
        if (value.length === 11) {
          value = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
        } else if (value.length === 10) {
          value = value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
        }

        form.value.phone = value
      }
    }

    const formatCpfInput = (event: Event) => {
      const target = event.target as HTMLInputElement
      let value = target.value.replace(/\D/g, '')
      if (value.length > 11) {
        value = value.slice(0, 11)
      }
      if (value.length <= 11) {
        if (value.length === 11) {
          value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
        }
        form.value.cpf = value
      }
    }

    const triggerPhotoUpload = () => {
      photoInput.value?.click()
    }

    const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp']

    const handlePhotoChange = async (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]

      if (!file) {
        return
      }

      if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
        openModal('Formato inválido', 'Use JPEG, PNG ou WebP.', 'error')
        target.value = ''
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        openModal('Arquivo grande demais', 'Escolha uma imagem de até 5MB.', 'error')
        target.value = ''
        return
      }

      const uid = currentUser.value?.userId
      if (!uid) {
        openModal('Sessão inválida', 'Faça login novamente para enviar a foto.', 'error')
        target.value = ''
        return
      }

      uploadingAvatar.value = true
      try {
        const user = await userService.uploadAvatar(file)
        applyUserToForm(user)
        form.value.photoUrl = user.photoUrl ? bustPhotoUrl(user.photoUrl) : ''
        form.value.avatar = ''
        form.value.phone = formatPhone(currentUser.value?.phone || form.value.phone || '')
        await store.dispatch('updateCurrentUser', mergeAuthUserFromBackend(user, currentUser.value, form.value))
        openModal('Foto atualizada', 'Sua foto de perfil foi enviada com sucesso.', 'avatarSuccess')
      } catch (err) {
        const message =
          err instanceof ApiException ? err.message : 'Não foi possível enviar a foto. Tente novamente.'
        openModal('Erro no envio', message, 'error')
      } finally {
        uploadingAvatar.value = false
        target.value = ''
      }
    }

    const removePhoto = async () => {
      const uid = currentUser.value?.userId
      if (!uid) {
        openModal('Sessão inválida', 'Faça login novamente.', 'error')
        return
      }

      removingAvatar.value = true
      try {
        const user = await userService.deleteAvatar(uid)
        applyUserToForm(user)
        form.value.photoUrl = user.photoUrl || ''
        form.value.avatar = ''
        form.value.phone = formatPhone(currentUser.value?.phone || form.value.phone || '')
        await store.dispatch('updateCurrentUser', mergeAuthUserFromBackend(user, currentUser.value, form.value))
        openModal('Foto removida', 'A foto do perfil foi removida.', 'success')
      } catch (err) {
        const message =
          err instanceof ApiException ? err.message : 'Não foi possível remover a foto. Tente novamente.'
        openModal('Erro', message, 'error')
      } finally {
        removingAvatar.value = false
      }
    }

    const applyUserToForm = (user: BackendUser) => {
      form.value.email = user.email
      form.value.username = user.username
      form.value.displayName = `${user.firstName} ${user.lastName}`.trim()
      form.value.cpf = user.cpf
      form.value.bornDate = bornDateToInput(user.bornDate)
      form.value.street = user.street ?? ''
      form.value.state = user.state ?? ''
      form.value.country = user.country ?? ''
      form.value.neighborhood = user.neighborhood ?? ''
      form.value.photoUrl = user.photoUrl || ''
    }

    const loadProfile = async () => {
      loadError.value = ''
      const uid = currentUser.value?.userId
      if (!uid) {
        loadError.value = 'Não foi possível identificar o usuário. Faça login novamente.'
        return
      }

      loadingProfile.value = true
      try {
        const user = await userService.getUserById(uid)
        applyUserToForm(user)
        form.value.phone = formatPhone(currentUser.value?.phone || '')
        form.value.companyName = currentUser.value?.companyName || ''
        form.value.role = currentUser.value?.role || ''
        form.value.notes = currentUser.value?.notes || ''
        form.value.photoUrl = user.photoUrl || ''
        if (form.value.photoUrl) {
          form.value.avatar = ''
        } else {
          form.value.avatar = currentUser.value?.avatar || ''
        }

        await store.dispatch('updateCurrentUser', mergeAuthUserFromBackend(user, currentUser.value, form.value))
      } catch (err) {
        const message =
          err instanceof ApiException ? err.message : 'Não foi possível carregar o perfil.'
        loadError.value = message
      } finally {
        loadingProfile.value = false
      }
    }

    const buildUpdatePayload = (): UpdateUserDto => {
      const payload: UpdateUserDto = {}

      const email = form.value.email.trim()
      if (email) {
        payload.email = email
      }

      const { firstName, lastName } = splitDisplayName(form.value.displayName)
      if (firstName || lastName) {
        payload.firstName = firstName
        payload.lastName = lastName
      }

      const username = form.value.username.trim()
      if (username) {
        payload.username = username
      }

      const cpfDigits = form.value.cpf.replace(/\D/g, '')
      if (cpfDigits.length > 0 && cpfDigits.length !== 11) {
        throw new Error('Informe um CPF válido com 11 dígitos ou deixe em branco.')
      }
      if (cpfDigits.length === 11) {
        payload.cpf = form.value.cpf
      }

      if (form.value.bornDate) {
        payload.bornDate = form.value.bornDate
      }

      const trimOrUndef = (s: string) => {
        const t = s.trim()
        return t ? t : undefined
      }

      payload.street = trimOrUndef(form.value.street)
      payload.state = trimOrUndef(form.value.state)
      payload.country = trimOrUndef(form.value.country)
      payload.neighborhood = trimOrUndef(form.value.neighborhood)

      return payload
    }

    const saveProfile = async () => {
      profileSaveError.value = ''
      const uid = currentUser.value?.userId
      if (!uid) {
        profileSaveError.value = 'Sessão inválida. Faça login novamente.'
        return
      }

      savingProfile.value = true
      try {
        let payload: UpdateUserDto
        try {
          payload = buildUpdatePayload()
        } catch (e) {
          profileSaveError.value = e instanceof Error ? e.message : 'Dados inválidos.'
          return
        }

        const updated = await userService.updateUser(uid, payload)
        applyUserToForm(updated)
        await store.dispatch('updateCurrentUser', mergeAuthUserFromBackend(updated, currentUser.value, form.value))
        openModal('Perfil atualizado', 'As informações do perfil foram salvas com sucesso.', 'success')
      } catch (err) {
        profileSaveError.value =
          err instanceof ApiException ? err.message : 'Erro ao salvar o perfil. Tente novamente.'
      } finally {
        savingProfile.value = false
      }
    }

    const savePassword = async () => {
      passwordError.value = ''

      if (!passwordForm.value.currentPassword || !passwordForm.value.newPassword || !passwordForm.value.confirmPassword) {
        passwordError.value = 'Preencha todos os campos de senha.'
        return
      }

      if (passwordForm.value.newPassword.length < 6) {
        passwordError.value = 'A nova senha deve ter pelo menos 6 caracteres.'
        return
      }

      if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
        passwordError.value = 'A confirmação da senha não confere.'
        return
      }

      const uid = currentUser.value?.userId
      if (!uid) {
        passwordError.value = 'Sessão inválida. Faça login novamente.'
        return
      }

      const email = (currentUser.value?.email || form.value.email || '').trim()
      if (!email) {
        passwordError.value = 'Email não encontrado para validar a senha atual.'
        return
      }

      savingPassword.value = true
      try {
        await authService.login({
          email,
          password: passwordForm.value.currentPassword
        })

        await userService.updateUser(uid, { password: passwordForm.value.newPassword })

        passwordForm.value.currentPassword = ''
        passwordForm.value.newPassword = ''
        passwordForm.value.confirmPassword = ''
        openModal('Senha atualizada', 'A nova senha foi registrada com sucesso.', 'success')
      } catch (err) {
        if (err instanceof ApiException && (err.status === 401 || err.status === 400)) {
          passwordError.value = 'Senha atual incorreta ou não foi possível validar.'
        } else if (err instanceof ApiException) {
          passwordError.value = err.message
        } else {
          passwordError.value = 'Não foi possível atualizar a senha.'
        }
      } finally {
        savingPassword.value = false
      }
    }

    form.value.phone = formatPhone(currentUser.value?.phone || '')
    if (currentUser.value?.cpf) {
      form.value.cpf = currentUser.value.cpf
    }
    if (currentUser.value?.bornDate) {
      form.value.bornDate = bornDateToInput(currentUser.value.bornDate)
    }

    onMounted(() => {
      void loadProfile()
    })

    return {
      currentUser,
      displayPhotoSrc,
      form,
      formatCpfInput,
      formatPhoneInput,
      initials,
      loadError,
      loadingProfile,
      modalMessage,
      modalTitle,
      modalVariant,
      passwordError,
      passwordForm,
      photoInput,
      profileSaveError,
      removePhoto,
      removingAvatar,
      savingPassword,
      savingProfile,
      showSuccessModal,
      handlePhotoChange,
      savePassword,
      saveProfile,
      triggerPhotoUpload,
      uploadingAvatar
    }
  }
}
</script>

<style scoped>
.perfil-page {
  width: min(1200px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 22px;
}

.profile-card {
  background: var(--profile-card-bg);
  border: 1px solid var(--profile-card-border);
  box-shadow: var(--profile-card-shadow);
  border-radius: 22px;
  padding: 22px;
}

.password-card {
  padding-top: 20px;
}

.card-head {
  margin-bottom: 18px;
}

.card-head h2 {
  color: var(--profile-heading-color);
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 6px;
}

.card-head p {
  color: var(--profile-subtext-color);
  font-size: 15px;
}

.profile-form,
.password-form {
  display: grid;
  gap: 18px;
}

.photo-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 4px;
}

.photo-avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  border: 1px solid var(--profile-avatar-border);
  background: var(--profile-avatar-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--profile-avatar-color);
  font-size: 30px;
  font-weight: 700;
  overflow: hidden;
  flex-shrink: 0;
}

.photo-avatar-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-meta {
  display: grid;
  gap: 10px;
}

.photo-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.photo-meta p {
  color: var(--profile-subtext-color);
  font-size: 14px;
}

.photo-btn {
  width: fit-content;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid var(--profile-photo-btn-border);
  background: var(--profile-photo-btn-bg);
  color: var(--profile-photo-btn-color);
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.photo-btn:hover:not(:disabled) {
  background: var(--profile-photo-btn-hover-bg);
  border-color: var(--profile-photo-btn-hover-border);
}

.photo-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.photo-btn-danger {
  border-color: color-mix(in srgb, #c94b4b 45%, var(--profile-photo-btn-border));
  color: #a33;
}

:global(#app.dark-theme) .photo-btn-danger {
  border-color: rgba(255, 143, 143, 0.35);
  color: #ffbcbc;
}

.hidden-input {
  display: none;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
}

.password-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: 14px 16px;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  color: var(--profile-label-color);
  margin-bottom: 8px;
  font-size: 14px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--profile-input-border);
  background: var(--profile-input-bg);
  color: var(--profile-input-color);
  font-size: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: var(--profile-placeholder-color);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: rgba(90, 142, 255, 0.7);
  box-shadow: 0 0 0 3px rgba(61, 115, 239, 0.14);
  background: var(--profile-input-focus-bg);
}

.form-group input:disabled {
  opacity: 1;
  color: var(--profile-disabled-color);
  background: var(--profile-disabled-bg);
}

.form-group textarea {
  min-height: 88px;
  resize: vertical;
}

.full-width {
  grid-column: 1 / -1;
}

.actions {
  display: flex;
  justify-content: flex-start;
}

.password-actions {
  padding-top: 2px;
}

.primary-action {
  padding: 12px 18px;
  border: none;
  border-radius: 12px;
  background: var(--profile-primary-bg);
  color: #f7f9ff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: var(--profile-primary-shadow);
  transition: transform 0.2s ease, filter 0.2s ease;
}

.primary-action:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.06);
}

.primary-action:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
  filter: none;
}

.profile-muted {
  color: var(--profile-subtext-color);
  font-size: 14px;
  margin: 0 0 4px;
}

.profile-banner {
  margin-bottom: 8px;
}

.inline-error {
  color: var(--profile-error-color);
  font-size: 14px;
  margin-top: -2px;
}

:global(#app) {
  --profile-card-bg:
    linear-gradient(180deg, rgba(247, 250, 255, 0.98), rgba(238, 244, 252, 0.96)),
    var(--bg-secondary);
  --profile-card-border: color-mix(in srgb, rgba(73, 121, 203, 0.22) 68%, var(--border-color));
  --profile-card-shadow: 0 18px 36px rgba(34, 56, 92, 0.08);
  --profile-heading-color: #20395f;
  --profile-subtext-color: #6e84a5;
  --profile-avatar-border: color-mix(in srgb, rgba(73, 121, 203, 0.22) 68%, var(--border-color));
  --profile-avatar-bg: linear-gradient(135deg, #f2f6ff, #e5eefc);
  --profile-avatar-color: #2f4f7c;
  --profile-photo-btn-border: color-mix(in srgb, rgba(73, 121, 203, 0.22) 68%, var(--border-color));
  --profile-photo-btn-bg: rgba(255, 255, 255, 0.82);
  --profile-photo-btn-color: #284267;
  --profile-photo-btn-hover-bg: rgba(255, 255, 255, 0.98);
  --profile-photo-btn-hover-border: rgba(73, 121, 203, 0.32);
  --profile-label-color: #5f7696;
  --profile-input-border: color-mix(in srgb, rgba(73, 121, 203, 0.2) 68%, var(--border-color));
  --profile-input-bg: rgba(255, 255, 255, 0.92);
  --profile-input-color: #243854;
  --profile-placeholder-color: #8a9ab2;
  --profile-input-focus-bg: #ffffff;
  --profile-disabled-color: #61799d;
  --profile-disabled-bg: rgba(236, 241, 249, 0.96);
  --profile-primary-bg: linear-gradient(135deg, #2f62c9, #3d86ff);
  --profile-primary-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 10px 20px rgba(24, 75, 163, 0.2);
  --profile-error-color: #c94b4b;
}

:global(#app.dark-theme) {
  --profile-card-bg:
    linear-gradient(180deg, rgba(31, 31, 31, 0.95), rgba(26, 26, 26, 0.92)),
    var(--bg-secondary);
  --profile-card-border: rgba(255, 255, 255, 0.07);
  --profile-card-shadow: 0 18px 36px rgba(0, 0, 0, 0.18);
  --profile-heading-color: #f5f7fb;
  --profile-subtext-color: #9ba9be;
  --profile-avatar-border: rgba(255, 255, 255, 0.08);
  --profile-avatar-bg: #171717;
  --profile-avatar-color: #eef3ff;
  --profile-photo-btn-border: rgba(255, 255, 255, 0.08);
  --profile-photo-btn-bg: rgba(255, 255, 255, 0.02);
  --profile-photo-btn-color: #f4f7ff;
  --profile-photo-btn-hover-bg: rgba(255, 255, 255, 0.06);
  --profile-photo-btn-hover-border: rgba(255, 255, 255, 0.14);
  --profile-label-color: #bfcce0;
  --profile-input-border: rgba(255, 255, 255, 0.08);
  --profile-input-bg: rgba(16, 16, 16, 0.72);
  --profile-input-color: #f5f7fb;
  --profile-placeholder-color: #6f7784;
  --profile-input-focus-bg: rgba(18, 18, 18, 0.9);
  --profile-disabled-color: #dbe7ff;
  --profile-disabled-bg: rgba(16, 16, 16, 0.72);
  --profile-primary-bg: linear-gradient(135deg, #2f4574, #294973);
  --profile-primary-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 10px 20px rgba(24, 51, 93, 0.2);
  --profile-error-color: #ff8f8f;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.modal-message {
  margin: 0;
}

.modal-message.success {
  color: #1f9d4c;
  font-weight: 600;
}

.modal-message.error {
  color: var(--profile-error-color);
  font-weight: 600;
}

/* Sucesso do upload no bucket: título, mensagem e rótulo do botão em branco */
.modal-content--avatar-upload-ok {
  background: linear-gradient(155deg, #0d2d24, #145a42);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
}

.modal-content--avatar-upload-ok h2,
.modal-content--avatar-upload-ok .modal-message.avatarSuccess {
  color: #fff;
  font-weight: 600;
}

.modal-content--avatar-upload-ok .modal-actions .btn.btn-primary {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.42);
}

.modal-content--avatar-upload-ok .modal-actions .btn.btn-primary:hover {
  background: rgba(255, 255, 255, 0.24);
  color: #fff;
}

@media (max-width: 900px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .password-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .profile-card {
    padding: 18px;
    border-radius: 18px;
  }

  .photo-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .photo-meta,
  .actions,
  .primary-action {
    width: 100%;
  }
}
</style>
